import { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Plus, RefreshCcw } from 'lucide-react'
import clsx from 'clsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { useBillingKeys, useContentFeeQuote, usePurchaseContentFee } from '@/hooks/admin/useContentFee'
import { BillingKeyRegistrationDialog } from './BillingKeyRegistrationDialog'
import { BillingKeySelector } from './BillingKeySelector'
import { freeTrialApi } from '@/api/freeTrials'
import { tenantApi } from '@/api/tenant'
import { useAuthStore } from '@/store/authStore'
import { getApiErrorCode } from '@/utils/apiError'
import { isLcOwner } from '@/utils/roleUtils'

interface Props {
  userId: number
  open: boolean
  onClose: () => void
  learnerStatusLabel?: string
}

function createPurchaseAttemptId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function ContentFeePurchaseModal({ userId, open, onClose, learnerStatusLabel }: Props) {
  const { t, i18n } = useTranslation(['admin', 'common'])
  const [selectedLevel, setSelectedLevel] = useState<string>('')
  const [selectedBillingKeyId, setSelectedBillingKeyId] = useState<number | null>(null)
  const [registrationOpen, setRegistrationOpen] = useState(false)
  const [memo, setMemo] = useState('')
  const [formalLicenseId, setFormalLicenseId] = useState<number | undefined>(undefined)
  const [staleNotice, setStaleNotice] = useState(false)
  const [quoteRequestedAfter, setQuoteRequestedAfter] = useState(Number.POSITIVE_INFINITY)
  const user = useAuthStore((state) => state.user)
  const canManageBillingKeys = isLcOwner(user ?? undefined)
  const queryClient = useQueryClient()

  useLayoutEffect(() => {
    if (open) {
      setQuoteRequestedAfter(Date.now())
      setSelectedLevel('')
      setSelectedBillingKeyId(null)
      setRegistrationOpen(false)
      setMemo('')
      setFormalLicenseId(undefined)
      setStaleNotice(false)
      // 환불·과정 변경 직후 이전 과정 견적을 잠시라도 렌더하지 않도록 캐시를 비우고 fresh fetch한다(spec §캐시 무효화).
      queryClient.removeQueries({ queryKey: ['contentFee', 'quote', userId] })
    } else {
      setQuoteRequestedAfter(Number.POSITIVE_INFINITY)
    }
  }, [open, queryClient, userId])

  const trialQ = useQuery({
    queryKey: ['lcFreeTrialHistory', userId],
    queryFn: async () => (await freeTrialApi.history(userId)).data.data ?? [],
    enabled: open,
  })
  // 전환 대상은 current(PENDING/ACTIVE) 회차뿐 — 종결(EXPIRED 포함) 회차는 일반 결제 흐름을 쓴다
  const trial = (trialQ.data ?? []).find((t) => t.status === 'PENDING' || t.status === 'ACTIVE') ?? null
  const isTrialConversion = !!trial
  const licensesQ = useQuery({
    queryKey: ['activeLicenses'],
    queryFn: async () => (await tenantApi.getAllActiveLicenses()).data.data ?? [],
    enabled: open && isTrialConversion,
  })
  const formalLicenses = licensesQ.data ?? []

  useEffect(() => {
    if (!isTrialConversion || formalLicenseId || formalLicenses.length === 0) return
    const recommended = formalLicenses.find(
      (license) => license.productId === trial?.productId,
    )
    setFormalLicenseId((recommended ?? formalLicenses[0]).id)
  }, [formalLicenseId, formalLicenses, isTrialConversion, trial?.productId])

  const quoteEnabled = open && !trialQ.isLoading && (!isTrialConversion || formalLicenseId != null)
  const quoteQ = useContentFeeQuote(userId, quoteEnabled, isTrialConversion ? formalLicenseId : undefined)
  const quoteIsFresh = open && !quoteQ.isFetching && quoteQ.dataUpdatedAt >= quoteRequestedAfter
  const quote = quoteIsFresh ? quoteQ.data : undefined
  const quoteLoading = !quoteQ.isError && (
    trialQ.isLoading || (quoteEnabled && (!quoteIsFresh || quoteQ.isLoading || quoteQ.isFetching))
  )
  const billingKeysQ = useBillingKeys(open)
  const billingKeys = billingKeysQ.data ?? []
  const usableBillingKeys = billingKeys.filter((card) => card.usable)
  const mutation = usePurchaseContentFee(userId)

  useEffect(() => {
    if (!quote || selectedLevel) return
    if (quote.derivable && quote.tiers.length > 0) {
      setSelectedLevel(quote.tiers[0].level)
    }
  }, [quote, selectedLevel])

  useEffect(() => {
    if (!open) return
    if (selectedBillingKeyId && usableBillingKeys.some((card) => card.id === selectedBillingKeyId)) return
    setSelectedBillingKeyId(usableBillingKeys[0]?.id ?? null)
  }, [open, selectedBillingKeyId, usableBillingKeys])

  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'
  const formatPrice = (price: number, currency: string) => {
    if (locale === 'ko-KR' && currency === 'KRW') {
      return `${price.toLocaleString(locale)} 원`
    }
    return `${price.toLocaleString(locale)} ${currency}`
  }

  const quoteBlocked = !!quote && !quote.derivable
  const tier = quote?.tiers.find((tr) => tr.level === selectedLevel) ?? null
  const isFree = tier?.price === 0
  const selectedBillingKey =
    usableBillingKeys.find((card) => card.id === selectedBillingKeyId) ?? null
  const hasBillingKey = billingKeys.length > 0
  const BillingKeyActionIcon = hasBillingKey ? RefreshCcw : Plus
  const canPay =
    canManageBillingKeys &&
    !!quote &&
    !quoteQ.isFetching &&
    !mutation.isPending &&
    !!tier &&
    (isFree || !!selectedBillingKey) &&
    (!isTrialConversion || formalLicenseId != null)

  const handlePay = () => {
    if (!canPay || !selectedLevel || !tier || !quote) return
    const billingKeyId = isFree ? null : selectedBillingKey?.id
    if (billingKeyId === undefined) return
    setStaleNotice(false)
    mutation.mutate(
      {
        level: selectedLevel,
        billingKeyId,
        purchaseAttemptId: createPurchaseAttemptId(),
        memo: memo.trim() ? memo.trim() : null,
        formalLicenseId: isTrialConversion ? formalLicenseId : undefined,
        quoteFingerprint: quote.quoteFingerprint ?? null,
      },
      {
        onSuccess: () => onClose(),
        onError: (error) => {
          // stale 견적(409) — 자동 재결제 없이 fresh quote를 다시 보여주고 재확인을 요구한다(spec §stale 견적 처리).
          if (getApiErrorCode(error) === 'CONTENT_FEE_QUOTE_STALE') {
            setStaleNotice(true)
            setSelectedLevel('')
            quoteQ.refetch()
          }
        },
      },
    )
  }

  // 축2 A — 확정 과정 외 현재 수강은 결제와 함께 종료된다. 결제 전에 담당자에게 알린다.
  const cancellingEnrollments = quote?.enrollmentImpact?.cancellingEnrollments ?? []
  const confirmedCourseName =
    quote?.enrollmentImpact?.confirmedProductName ?? quote?.contentTitle ?? null

  const reasonHint =
    quoteBlocked && quote?.reason
      ? t(`admin:students.detail.contentFee.modal.reason.${quote.reason}`, {
          status: learnerStatusLabel,
          defaultValue: t('admin:students.detail.contentFee.modal.quoteBlocked'),
        })
      : null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md p-0 gap-0 grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader className="px-5 py-4 border-b pr-10">
          <DialogTitle>{t('admin:students.detail.contentFee.modal.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-5 py-4 overflow-y-auto">
          {quoteLoading && (
            <p className="text-sm text-gray-500">{t('admin:students.detail.contentFee.modal.quoteLoading')}</p>
          )}
          {quoteQ.isError && (
            <p className="text-sm text-red-600">{t('admin:students.detail.contentFee.modal.quoteError')}</p>
          )}
          {staleNotice && (
            <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-700">
                {t('admin:students.detail.contentFee.modal.quoteStale')}
              </p>
            </div>
          )}

          {isTrialConversion && (
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t('admin:students.detail.contentFee.modal.formalCourse')}
              </label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={formalLicenseId ?? ''}
                onChange={(event) => {
                  setFormalLicenseId(event.target.value ? Number(event.target.value) : undefined)
                  setSelectedLevel('')
                }}
                disabled={licensesQ.isLoading || mutation.isPending}
              >
                <option value="">{t('admin:students.detail.contentFee.modal.formalCoursePlaceholder')}</option>
                {formalLicenses.map((license) => (
                  <option key={license.id} value={license.id}>
                    {license.productName ?? license.curriculumTitle ?? `#${license.productId}`}
                    {license.productId === trial?.productId
                      ? ` (${t('admin:students.detail.contentFee.modal.recommended')})`
                      : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                {t('admin:students.detail.contentFee.modal.formalCourseHint')}
              </p>
            </div>
          )}

          {cancellingEnrollments.length > 0 && (
            <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-1 text-xs text-amber-800">
                <p className="font-medium">
                  {t('admin:students.detail.contentFee.modal.enrollmentSwitchTitle')}
                </p>
                <p>
                  {t('admin:students.detail.contentFee.modal.enrollmentSwitchBody', {
                    cancelling: cancellingEnrollments
                      .map((enrollment) => enrollment.productName ?? `#${enrollment.licenseId}`)
                      .join(', '),
                    confirmed: confirmedCourseName ?? '',
                  })}
                </p>
                <p>{t('admin:students.detail.contentFee.modal.enrollmentSwitchHistoryKept')}</p>
              </div>
            </div>
          )}

          {quote?.contentTitle && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{t('admin:students.detail.contentFee.modal.contentTitle')}</span>
              <span className="font-medium text-gray-800">{quote.contentTitle}</span>
            </div>
          )}

          {quote && quote.derivable && quote.tiers.length > 0 && (
            <div role="radiogroup" aria-labelledby="content-fee-tier-label" className="space-y-2">
              <span id="content-fee-tier-label" className="block text-sm font-medium text-gray-700">
                {t('admin:students.detail.contentFee.modal.tierLabel')}
              </span>
              {quote.tiers.map((tr) => (
                <button
                  key={tr.level}
                  type="button"
                  role="radio"
                  aria-checked={selectedLevel === tr.level}
                  onClick={() => setSelectedLevel(tr.level)}
                  className={clsx(
                    'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left',
                    selectedLevel === tr.level
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 hover:border-gray-300',
                  )}
                >
                  <span className="font-medium text-gray-900">{tr.level}</span>
                  <span className="text-sm text-gray-600">
                    {t('admin:students.detail.contentFee.modal.lessonsCount', { count: tr.grantedLessons })}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(tr.price, tr.currency)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {quoteBlocked && (
            <>
              {reasonHint && (
                <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">{reasonHint}</p>
                </div>
              )}
            </>
          )}

          {!isFree && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="block text-sm font-medium text-gray-700">
                  {t('admin:students.detail.contentFee.billingKeys.label')}
                </span>
                {canManageBillingKeys ? (
                  <button
                    type="button"
                    onClick={() => setRegistrationOpen(true)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <BillingKeyActionIcon className="h-3.5 w-3.5" />
                    {t(
                      hasBillingKey
                        ? 'admin:students.detail.contentFee.billingKeys.replace'
                        : 'admin:students.detail.contentFee.billingKeys.add',
                    )}
                  </button>
                ) : null}
              </div>
              <BillingKeySelector
                billingKeys={billingKeys}
                selectedBillingKeyId={selectedBillingKeyId}
                onSelectBillingKey={setSelectedBillingKeyId}
                isLoading={billingKeysQ.isLoading}
                disabled={mutation.isPending}
                canDelete={canManageBillingKeys}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.detail.contentFee.modal.memo')}
            </label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder={t('admin:students.detail.contentFee.modal.memoPlaceholder')}
            />
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 px-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={handlePay}
            disabled={!canPay}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {mutation.isPending
              ? t(
                  isFree
                    ? 'admin:students.detail.contentFee.modal.freePurchasing'
                    : 'admin:students.detail.contentFee.modal.paying',
                )
              : t(
                  isFree
                    ? 'admin:students.detail.contentFee.modal.freePurchase'
                    : 'admin:students.detail.contentFee.modal.pay',
                )}
          </button>
        </DialogFooter>
      </DialogContent>
      {!isFree && canManageBillingKeys && (
        <BillingKeyRegistrationDialog
          open={registrationOpen}
          onClose={() => setRegistrationOpen(false)}
        />
      )}
    </Dialog>
  )
}
